import React, { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ContentLayout } from '@app/layouts'
import { ExtendedTable, Header, SubtypesSelect, TableSettingsToolbar } from '@app/components'

import { MONITORINGS_SUBTYPES_KEY, MONITORINGS_TYPES_WITH_SUBTYPES_KEY } from '@app/constants/queryKeys'
import { SIZE } from '@app/constants'

import { sorterDispatcher } from '@app/utils/table'
import { getFilterFromEntities } from '@app/utils/entitiesAdapters/getFilterFromEntities'
import { getSelectedValues } from '@app/utils/filters'
import {
  getMonitoringsTypesWithSubtypes,
  getSubtypesTypes,
  patchMonitoringsTypeWithSubtypes
} from '@app/api/routes/monitorings'

import { usePaginationQueryParams } from '@app/hooks'

import { adaptManagingSubtypesTableData } from './lib'

import type { Column } from '@app/components/base/Table/types'
import type { PatchMonitoringTypeData } from '@app/api/routes/monitorings/types'
import type { ManagingSubtypesDataSource } from './types'

const title = 'Управление подтипами в шаблоне'

const toolbar = <TableSettingsToolbar key='table-settings-toolbar' />

export const ManagingSubtypesPage: React.FC = () => {
  const queryClient = useQueryClient()

  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()

  const { data: subtypesTypesData, isLoading: isLoadingSubtypesTypesData } = useQuery({
    queryKey: [MONITORINGS_SUBTYPES_KEY.subtypesItems],
    queryFn: async () => await getSubtypesTypes()
  })

  const subtypesTypesDataOptions = useMemo(() => getFilterFromEntities(subtypesTypesData), [isLoadingSubtypesTypesData])

  const { data: managingSubtypesTableData, isLoading: isManagingSubtypesDataLoading } = useQuery({
    queryKey: [MONITORINGS_TYPES_WITH_SUBTYPES_KEY.monitoringsTypesWithSubtypesItems, paginationParams],
    queryFn: async () => await getMonitoringsTypesWithSubtypes(paginationParams),
    select: monitoringsTypesWithSubtypesData => adaptManagingSubtypesTableData(monitoringsTypesWithSubtypesData)
  })

  const patchMonitoringTypeMutation = useMutation({
    mutationFn: patchMonitoringsTypeWithSubtypes,
    onSuccess: async () =>
      await queryClient.invalidateQueries({
        queryKey: [MONITORINGS_TYPES_WITH_SUBTYPES_KEY.monitoringsTypesWithSubtypesItems]
      })
  })

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handlePatchMonitoringType = ({ id, ...body }: PatchMonitoringTypeData): void => {
    patchMonitoringTypeMutation.mutate({
      id,
      ...body
    })
  }

  const columns: Array<Column<ManagingSubtypesDataSource>> = [
    {
      key: 'monitoringType',
      dataIndex: 'monitoringType',
      title: 'Тип мониторинга',
      width: '50%',
      sorter: (a, b) => sorterDispatcher(a.monitoringType, b.monitoringType)
    },
    {
      key: 'subtypes',
      dataIndex: 'subtypes',
      title: 'Подтипы',
      width: '50%',
      render: (_, { subtypes, key }) => {
        const defaultValue = getFilterFromEntities(subtypes)

        return (
          !isLoadingSubtypesTypesData && (
            <SubtypesSelect
              loading={ isLoadingSubtypesTypesData }
              defaultValue={ defaultValue }
              options={ subtypesTypesDataOptions }
              mode='multiple'
              size={ SIZE.small }
              placeholder='Выберите подтипы'
              placement='bottomRight'
              onAccept={ selectedOptions => {
                handlePatchMonitoringType({
                  id: key,
                  monitoringSubtypeIds: getSelectedValues(selectedOptions)
                })
              } }
              strictValue
            />
          )
        )
      }
    }
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <ContentLayout>
        <ExtendedTable
          loading={ isManagingSubtypesDataLoading }
          columns={ columns }
          dataSource={ managingSubtypesTableData?.dataSource }
          toolbar={ toolbar }
          pagination={ {
            ...pagination,
            total: managingSubtypesTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>
    </>
  )
}
